import React, { FC } from "react";
import { useSelector } from 'react-redux';
import TableTab from "../components/tabs/table-tab";
import { USER_LOGINS } from "../constants/menu-types";
import { useLocale } from "../contexts/localeContext";
import { TableContextProvider } from "../contexts/table/TableContextProvider";
import { TranslationKeys } from "../contexts/TranslationKeys";
import { Category } from "../models/Category";
import columns from '../models/ColumnDefinitions/UserLoginColumns';
import { Lookup } from "../models/Lookup";
import LookupTypes from "../models/LookupTypes";
import SiteSearch from "../models/search/SiteSearch";
import searchColumns from '../models/search/UserLoginSearch';
import { DeleteEntityResult } from "../models/services/DeleteEntityResult";
import { deleteUserLogin, getUserLogins } from "../services/loginService";
import { getSiteNamesWithPagination } from "../services/siteService";
import { getNumbers } from "../utilities/comparer";
import { IAppState } from "../store/types";
import { Filter } from "../models/Filter";
import TableContextStateMonitor from "../contexts/table/TableContextStateMonitor";
import {getLoginTypeLookup} from "@/services/utilities/loginsUtils.ts";

interface UserLoginsTabProps {
    setBusy: (isBusy: boolean) => void,
    categories: Category[],
    setCategories: (category: Category[]) => void,
    onFilterChange: (filters: Filter[]) => void
}

const UserLoginsTab: FC<UserLoginsTabProps> = ({ setBusy, categories, setCategories, onFilterChange }) => {
    const { translate } = useLocale();
    const title = translate.byKey(TranslationKeys.logins);
    const itemPath = '/access-control/operators/login/';
    const user = useSelector((state: IAppState) => (state.userInfo));
    const lookup: Lookup[] = [
        {
            name: LookupTypes.site,
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: "userLoginTypeId",
            values: getLoginTypeLookup()
        }
    ];

    const onDelete = (id: string): Promise<DeleteEntityResult> => {
        setBusy(true);
        return new Promise((resolve, reject) => {
            deleteUserLogin(id)
                .then(
                    deleteResult => {
                        resolve(deleteResult);
                        setCategories([{ ...categories[0], count: (categories[0]?.count ?? 1) - 1 }]);
                        setBusy(false);
                    },
                    deleteResult => {
                        reject(deleteResult);
                        setBusy(false);
                    })
        });
    }

    const range = getNumbers(1, 50).map((item) => item.toString()) as any

    const userLoginId = useSelector((state: any) => (state.userInfo.id))
    const currentMenuDetail = USER_LOGINS;
    const initialiseUserPreferences = (setDetails: (userLoginId, currentMenuDetail) => void) => {
        setDetails(userLoginId, currentMenuDetail);
    }

    return <TableContextProvider
        initialiser={(setDetails) => initialiseUserPreferences(setDetails)}
        dataMethod={listparameters => getUserLogins(listparameters, parseInt(user.profileId))}
        tableDescriptor={columns}
        searchDescriptor={searchColumns}
        useTable
        usePagination
        useSearch
        useSort
        useFilter
        deleteMethod={onDelete}
        lookup={lookup}
        customDeleteWithRange={{ fieldName: "id", excludedValues: range }}>
        <TableContextStateMonitor onFilterChange={onFilterChange} />
        <TableTab
            title={title}
            selectRows={false}
            rowPath={itemPath}
            cyName="userLogins"/>
    </TableContextProvider>
};

export default UserLoginsTab;
